"use client"

import { React, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Plus, AlertCircle, CheckCircle, MapPin, Tag, Clock } from "lucide-react"
import { usePostProblem } from "../hooks/usePostProblem"
import { Button, Modal, Form, Alert, Card } from "react-bootstrap"
import { useDarkMode } from "../hooks/useDarkMode"
import "../styles/components/PostProblem.css"

export const PostProblem = ({ groupId, onProblemCreated }) => {
  const { isAuthenticated, user } = useAuth0()
  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isUrgent: false,
    location: "",
    category: "",
  })

  const { postProblem, loading, error, success } = usePostProblem()
  const { isDarkMode } = useDarkMode()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, isUrgent: e.target.checked }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.title || !formData.description) {
      return
    }

    const problemData = {
      ...formData,
      groupId,
      createdBy: user?.name || user?.email || "Anonymous",
      createdOn: new Date().toISOString(),
      isSolved: false,
    }

    const postedProblem = await postProblem(problemData)

    if (postedProblem) {
      onProblemCreated?.(postedProblem)

      // Reset form after successful submission
      setFormData({
        title: "",
        description: "",
        isUrgent: false,
        location: "",
        category: "",
      })

      setShowModal(false)
    }
  }

  const handleClose = () => {
    setShowModal(false)
    // Reset form when closing
    setFormData({
      title: "",
      description: "",
      isUrgent: false,
      location: "",
      category: "",
    })
  }

  const handleShow = () => setShowModal(true)

  if (!isAuthenticated) {
    return null
  }

  const darkModeClass = isDarkMode ? "dark-mode-modal" : ""

  return (
    <div className="mt-4">
      <Button
        variant="primary"
        onClick={handleShow}
        className="group-wide-cta"
      >
        <Plus className="me-2" size={18} /> Report a Problem or Need
      </Button>

      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        dialogClassName="problem-modal-width modal-dialog-centered"
        contentClassName={darkModeClass}
      >
        <Modal.Header closeButton closeVariant={isDarkMode ? "white" : undefined} className="border-0 pb-0">
          <Modal.Title className="w-100 text-center fw-bold fs-2">Report a Problem or Need</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-center mb-4">Share details about an issue that needs attention in your community</p>

          <Form onSubmit={handleSubmit}>
            <Card className={`mb-3 ${darkModeClass}`}>
              <Card.Body>
                <Form.Group>
                  <Form.Label className="fw-bold text-center w-100 mb-2">Title</Form.Label>
                  <Form.Control
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="E.g., 'Elderly Mrs. Donnely's house needs a new roof'"
                    required
                    className={darkModeClass}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className={`mb-3 ${darkModeClass}`}>
              <Card.Body>
                <Form.Group>
                  <Form.Label className="fw-bold text-center w-100 mb-2">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Provide details about the problem or need..."
                    required
                    className={darkModeClass}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className={`mb-3 ${darkModeClass}`}>
              <Card.Body>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <MapPin size={16} className="me-1" />
                        <span className="fw-bold">Location (Optional)</span>
                      </Form.Label>
                      <Form.Control
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Where is this issue located?"
                        className={darkModeClass}
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="d-flex align-items-center">
                        <Tag size={16} className="me-1" />
                        <span className="fw-bold">Category (Optional)</span>
                      </Form.Label>
                      <Form.Control
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        placeholder="E.g., Infrastructure, Environment, etc."
                        className={darkModeClass}
                      />
                    </Form.Group>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className={`mb-3 ${darkModeClass}`}>
              <Card.Body>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="isUrgent"
                    label={
                      <span className="d-flex align-items-center">
                        <Clock size={16} className="me-1 text-danger" />
                        <span>This is an urgent issue</span>
                      </span>
                    }
                    checked={formData.isUrgent}
                    onChange={handleCheckboxChange}
                    className={isDarkMode ? "text-white" : ""}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {error && (
              <Alert variant="danger" className="d-flex align-items-center">
                <AlertCircle className="me-2" size={18} />
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="d-flex align-items-center">
                <CheckCircle className="me-2" size={18} />
                Problem reported successfully!
              </Alert>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 d-flex justify-content-end">
          <Button variant="secondary" onClick={handleClose} className="me-2">
            Cancel
          </Button>
          <Button variant="success" onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting..." : "Submit Problem"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PostProblem
